/**
 * NotificationPanel (13-27): the regulator notices on an incident (FIELDSET §3.3.1's notification verb, 19 columns,
 * NDIS and SIRS here; WorkSafe is HR & WHS's own panel, 13-28) and the regulator exchanges as their §3.4.1 rows.
 *
 * - **Shown only once the decision has a notice** (`isNotifiable`): NDIS for `NDIS-Reportable`, SIRS for
 *   `SIRS-P1` / `SIRS-P2`, and a WorkSafe line for `WorkSafe` or `WorkSafeNotifiable` = Yes.
 * - **One group per regulator, each its decider's** (D-13-L-2, D-13-22-5): the NDIS notice, any Oversight member; the
 *   SIRS notice, the SaH decider. The group's columns are the notification verb's own list split by notice
 *   (`NOTICE_GROUPS`, 13-20's allow-list). Anyone else reads the group, with who records it.
 * - **Due versus done:** each notice shows its due instant from `notificationDeadlines`, with its OB row, and reads
 *   amber (never red, D-13-15-4) once past due while its column is blank. The final report's due day is pre-filled by
 *   the rules (84 days SIRS, 60 business days NDIS), editable for the Commissioner's own period.
 * - **One notice per visit** (D-13-20-5), with what the notice itself carries (D-13-27-5). The notifier defaults to
 *   the signed-in person (shown, editable); the position is plain text, never a name (FIELDSET §1.5).
 * - **The regulator rows** (§3.4.1 `Notification`, `InformationRequest`, `RegulatorRequirement`,
 *   `RegulatorDecision`): one button each, through 13-26's `SiblingComposer` with the kind fixed, saved under the
 *   notification verb and the group's notice.
 * The panel never changes `ReportabilityDecision`. Practice mode: the practice writer stores nothing on SharePoint.
 */

import { useMemo, useState } from 'react';
import VerbForm from '../VerbForm';
import type { VerbControlProps, VerbFormControl } from '../VerbForm';
import SiblingComposer from '../SiblingComposer';
import Lines from '../Lines';
import { Button, Select } from '../../ui';
import { liveChoice } from '../../../config/fieldset/live';
import { choicesOf, fieldOf } from '../../../config/fieldset/registerFields';
import { formatDay, formatInstant, melbourneWallToInstant } from '../../../lib/fieldValues';
import { finalReportDueOn } from '../../../lib/notificationDeadlines';
import type { DeadlineRule } from '../../../lib/notificationDeadlines';
import { SAH_DELEGATES_LINE, canDoRecordVerb } from '../../../lib/recordAuthority';
import type { NoticeKind } from '../../../lib/recordAuthority';
import { valueOf } from '../../../lib/recordView';
import { composerKinds } from '../../../lib/siblingComposer';
import { WRITER_OF_VERB } from '../../../lib/recordVerbs';
import { NOT_RECORDED, timeKeyOf } from '../../../lib/verbForm';
import type { FormValues } from '../../../lib/verbForm';
import { NOTICE_GROUPS, REGIME_WORDS, noticeRules, noticeState, noticesFor } from '../../../lib/regulatoryPanels';
import type { NoticeState } from '../../../lib/regulatoryPanels';
import type { RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import record from '../record.module.css';
import styles from './NotificationPanel.module.css';

const I = 'incidents';
type Group = Exclude<NoticeKind, 'worksafe'>;

/** The plan's button words for the four regulator rows (words only: the stored values are the live `LogKind`s). */
const ROW_BUTTONS: Readonly<Record<string, string>> = {
  Notification: 'Log a notification',
  InformationRequest: 'Regulator asked for information',
  RegulatorRequirement: 'Regulator set a requirement',
  RegulatorDecision: 'Regulator decision',
};

/** The notification verb's rows on Incidents (§3.4.1, writer `NOT`): exactly the four the plan names. */
const ROW_KINDS = composerKinds(I, WRITER_OF_VERB.notification);
if (ROW_KINDS.length !== Object.keys(ROW_BUTTONS).length || !ROW_KINDS.every((k) => k.logKind && ROW_BUTTONS[k.logKind])) {
  throw new Error('NotificationPanel: the notification verb’s §3.4.1 rows are not the four regulator rows');
}

const NARRATIVE_HINT: Readonly<Record<string, string>> = {
  ExternalOutcome: 'The outcome in words. No phone numbers or email addresses here (FIELDSET §1.6).',
  FurtherActionsProposed: 'What Harmony proposes to do next. No phone numbers or email addresses here.',
  InfoWithheld: 'Only when information was withheld under IMRI s 22: which item, and why.',
};

const [FINAL_YES] = (fieldOf(I, 'FinalReportDueOn')?.required.notification as { in?: readonly string[] } | undefined)?.in ?? [];

const [CLASS_5BD] = liveChoice(I, 'NdisNoticeClass', '5 business days');

function whoRecords(group: Group): string {
  if (group === 'ndis') return 'Oversight records the NDIS notices.';
  // D-13-L-2 / D-13-33: the decider, then the delegates in order, by role.
  return ['The SaH reportability decider records the SIRS notices.', SAH_DELEGATES_LINE].filter(Boolean).join(' ');
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** The citation and caveats of a rule, in small words. */
function Cite({ rule }: { rule: DeadlineRule }) {
  return (
    <span className={styles.cite}>
      {' '}
      {rule.obRow ?? 'No obligations row'} · {rule.section}.{rule.note ? ` ${rule.note}` : ''}
      {rule.provisional ? ` Provisional: ${rule.provisional}.` : ''}
    </span>
  );
}

/** One notice's due line: given, overdue (amber), or due. */
function DueLine({ state }: { state: NoticeState }) {
  const { rule, given, overdue } = state;
  const when = formatInstant(rule.dueAt);
  if (given) {
    const late = Date.parse(given) > Date.parse(rule.dueAt);
    return (
      <p className={record.hint}>
        Given {formatInstant(given)}; it was due {when} ({rule.reading}){late ? ', so it was given after the due time' : ''}.
        <Cite rule={rule} />
      </p>
    );
  }
  if (overdue) {
    return (
      <p className={record.notice} role="status">
        <strong>Overdue.</strong> It was due {when} ({rule.reading}).
        <Cite rule={rule} />
      </p>
    );
  }
  return (
    <p className={record.hint}>
      {rule.conditional ? `If needed: due ${when}. ${rule.conditional}` : `Due ${when} (${rule.reading}).`}
      <Cite rule={rule} />
    </p>
  );
}

/** A typed notice instant, as the engine would read it (for the final report's pre-fill). */
function typedInstant(values: FormValues, column: string): string | null {
  const day = values[column] ?? '';
  const time = values[timeKeyOf(column)] ?? '';
  if (!day || !time) return null;
  const r = melbourneWallToInstant(day, time);
  return r.ok ? r.value : null;
}

function finalFor(group: Group, row: RecordRow, values: FormValues | null) {
  const notices: Record<string, unknown> = { ...row.fields };
  if (values) {
    for (const column of NOTICE_GROUPS[group]) {
      const typed = typedInstant(values, column);
      if (typed) notices[column] = typed;
    }
  }
  return finalReportDueOn({ regime: group, notices });
}

function ReadOnlyGroup({ group, row, states }: { group: Group; row: RecordRow; states: NoticeState[] }) {
  return (
    <div className={styles.group}>
      <p className={styles.who}>
        <strong>{whoRecords(group)}</strong> You can read them here.
      </p>
      {states.map((s) => (
        <div key={s.rule.key} className={styles.dueItem}>
          <p className={record.label}>{fieldOf(I, s.rule.notice ?? '')?.label ?? s.rule.notice}</p>
          <DueLine state={s} />
        </div>
      ))}
      <dl className={record.facts}>
        {NOTICE_GROUPS[group].map((column) => {
          const spec = fieldOf(I, column)!;
          const v = valueOf(spec, row);
          return (
            <div key={column} style={{ display: 'contents' }}>
              <dt>{spec.label}</dt>
              <dd>{v.value === null ? <span className={record.blank}>{NOT_RECORDED}</span> : spec.kind === 'textarea' ? <Lines text={v.value} /> : v.value}</dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function NoticeGroup({ group, row, siblings, actor, readOnly, onSaved }: PanelProps & { group: Group }) {
  const may = !readOnly && canDoRecordVerb('notification', row, actor, { notice: group }).ok;
  const [now] = useState(() => new Date());
  const [rowKind, setRowKind] = useState<string | null>(null);
  const rules = useMemo(() => noticeRules(row).filter((r) => r.regime === group), [row, group]);
  const states = rules.map((r) => noticeState(r, row, '', now));
  const fiveDayClass = group === 'ndis' && row.fields.NdisNoticeClass === CLASS_5BD;
  const fixedKinds = useMemo(() => Object.fromEntries(ROW_KINDS.map((k) => [k.logKind as string, [k]])), []);

  const defaults = useMemo(() => {
    const out: Record<string, string> = {};
    if (str(row.fields.NotifierUPN) === '') out.NotifierUPN = actor.upn.toLowerCase();
    const fin = finalFor(group, row, null);
    if (FINAL_YES && row.fields.FinalReportRequired === FINAL_YES && str(row.fields.FinalReportDueOn) === '' && fin) out.FinalReportDueOn = fin.dueOn;
    return out;
  }, [row, actor.upn, group]);

  if (!may) return <ReadOnlyGroup group={group} row={row} states={states} />;

  const fieldNote = (column: string, values: FormValues, control: VerbFormControl) => {
    const rule = rules.find((r) => r.notice === column);
    if (rule) {
      return (
        <>
          <DueLine state={noticeState(rule, row, values[column] ?? '', now)} />
          {column === 'NdisFurtherNoticeAt' && fiveDayClass && (
            <p className={record.hint}>
              For the 5-business-day class this is the one notice (IMRI s 21), recorded here. The daily escalation still
              looks at the initial-notice column for this class (ISS-267), so it may list this incident after the notice
              is given.
            </p>
          )}
        </>
      );
    }
    if (column === 'NdisInitialNoticeAt' && fiveDayClass) {
      return <p className={record.hint}>No 24-hour notice is due for the 5-business-day class. Record its notice under the 5-business-day notice.</p>;
    }
    if (column === 'FinalReportDueOn') {
      const fin = finalFor(group, row, values);
      if (!fin) return <p className={record.hint}>The rules give a due day once a notice is recorded.</p>;
      return (
        <p className={record.hint}>
          By the rules: {formatDay(fin.dueOn)} ({fin.reading}).
          <span className={styles.cite}>
            {' '}
            {fin.obRow} · {fin.section}.{fin.note ? ` ${fin.note}` : ''}
            {fin.provisional ? ` Provisional: ${fin.provisional}.` : ''}
          </span>{' '}
          {values.FinalReportDueOn !== fin.dueOn && (
            <Button type="button" variant="ghost" size="sm" onClick={() => control.set('FinalReportDueOn', fin.dueOn)}>
              Use {formatDay(fin.dueOn)}
            </Button>
          )}
        </p>
      );
    }
    if (column === 'NotifierUPN') return <p className={record.hint}>The person who gave the notice. It starts as you.</p>;
    if (NARRATIVE_HINT[column]) return <p className={record.hint}>{NARRATIVE_HINT[column]}</p>;
    return null;
  };

  /** Final report required? Answering Yes fills the due day by the rules when it is blank (editable after). */
  const renderControl = (column: string, control: VerbControlProps) => {
    if (column !== 'FinalReportRequired') return null;
    return (
      <Select
        id={control.id}
        value={control.value}
        className={control.refused ? record.inputRefused : undefined}
        aria-describedby={control.describedBy}
        onChange={(e) => {
          const v = e.target.value;
          control.set(v);
          const fin = finalFor(group, row, control.values);
          if (v === FINAL_YES && fin && !(control.values.FinalReportDueOn ?? '')) control.form.set('FinalReportDueOn', fin.dueOn);
        }}
      >
        <option value="">Not answered</option>
        {choicesOf(I, column).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </Select>
    );
  };

  return (
    <div className={styles.group}>
      <p className={styles.who}>
        <strong>{whoRecords(group)}</strong> Record one notice at a time; a blank notice means it hasn&rsquo;t been
        given yet.
      </p>
      <VerbForm
        register={I}
        verb="notification"
        row={row}
        siblings={siblings}
        actor={actor}
        columns={NOTICE_GROUPS[group]}
        extraInputs={{ ctx: { notice: group } }}
        defaults={defaults}
        fieldNote={fieldNote}
        renderControl={renderControl}
        saveLabel="Save the notice"
        onSaved={onSaved}
      />
      <div className={styles.rowsBlock}>
        <p className={record.label}>The regulator&rsquo;s side</p>
        <p className={record.hint}>Each is its own row on the trail, under your name. Write what happened; contact details go in their own fields.</p>
        <div className={styles.rowButtons}>
          {ROW_KINDS.map((k) => (
            <Button
              key={k.key}
              type="button"
              variant="secondary"
              size="sm"
              className={rowKind === k.logKind ? styles.rowPicked : undefined}
              aria-pressed={rowKind === k.logKind}
              onClick={() => setRowKind(rowKind === k.logKind ? null : (k.logKind as string))}
            >
              {ROW_BUTTONS[k.logKind as string]}
            </Button>
          ))}
        </div>
        {rowKind && (
          <SiblingComposer
            key={rowKind}
            row={row}
            actor={actor}
            verb="notification"
            kinds={fixedKinds[rowKind]}
            gate={{ notice: group }}
            title={ROW_BUTTONS[rowKind]}
            intro={`${REGIME_WORDS[group]}. One row on the trail, under your name, dated when it happened.`}
            onSaved={(saved) => {
              setRowKind(null);
              onSaved(saved);
            }}
          />
        )}
      </div>
    </div>
  );
}

/** HR & WHS's notice (13-28's panel): who records it and when it is due. */
function WorkSafeLine({ row }: { row: RecordRow }) {
  const [now] = useState(() => new Date());
  const rules = noticeRules(row).filter((r) => r.regime === 'worksafe');
  return (
    <div className={styles.group}>
      <p className={styles.who}>
        <strong>HR &amp; WHS records the WorkSafe notice</strong>, in the <a href="#panel-worksafe">WorkSafe notice panel</a>.
      </p>
      {rules.map((r) => (
        <div key={r.key} className={styles.dueItem}>
          <p className={record.label}>{fieldOf(I, r.notice ?? '')?.label ?? r.notice}</p>
          <DueLine state={noticeState(r, row, '', now)} />
        </div>
      ))}
    </div>
  );
}

export default function NotificationPanel(props: PanelProps) {
  const { row } = props;
  const notices = noticesFor(row);
  const groups = notices.filter((n): n is Group => n !== 'worksafe');
  return (
    <div className={record.verbForm}>
      {groups.map((group) => (
        <section key={group} className={styles.regime} aria-label={REGIME_WORDS[group]}>
          <h4 className={styles.regimeTitle}>{REGIME_WORDS[group]}</h4>
          <NoticeGroup {...props} group={group} />
        </section>
      ))}
      {notices.includes('worksafe') && (
        <section className={styles.regime} aria-label={REGIME_WORDS.worksafe}>
          <h4 className={styles.regimeTitle}>{REGIME_WORDS.worksafe}</h4>
          <WorkSafeLine row={row} />
        </section>
      )}
    </div>
  );
}
